import { Button, IconButton } from "@/src/components/buttons";
import { openDrawer } from "@/src/components/dialogs";
import { Input } from "@/src/components/universalInput";
import {
  ConditionGroup,
  DocumentCondition,
  DocumentVariable,
  Field,
  FieldAnswer,
  FieldsSection,
  Patient,
  Reference,
  conditionOperators,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import { getUserId, jsonAbstract, uid } from "@/src/utils/utils";
import { useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { VariableInputPicker } from "./variableInputPicker";
import { evalConditionGroup } from "./variablesHook";

function ConditionGroupPicker({
  sections,
  current,
  level,
  onChange,
}: {
  sections: (FieldsSection | null | undefined)[];
  current: ConditionGroup;
  level: number;
  onChange: (newValue: ConditionGroup) => void;
}) {
  const [group, setGroup] = useState<ConditionGroup>(current);

  function onValueChange(newValue: ConditionGroup) {
    setGroup({ ...newValue });
    onChange({ ...newValue });
  }

  return (
    <div className=" c1 gap10">
      <div
        className="br p10 c1 gap10"
        style={{
          background:
            group?.type == "and" ? "rgb(0,0,0,0.08)" : "rgb(0, 164, 255, 0.2)",
        }}
      >
        {group.conditions?.map((conditionOrGroup, index) => {
          let button = (
            <div style={{ width: "50px" }}>
              <Button
                type={"tertiary"}
                onClick={() => {
                  group.conditions?.splice(index + 1, 0, {
                    id: uid(),
                    category: "condition",
                    a: "",
                    operator: "=",
                    b: "",
                    creator: getUserId(),
                  });
                  onValueChange(group);
                }}
              >
                {group.type == "and" ? "Et" : "Ou"}
              </Button>
            </div>
          );

          if (conditionOrGroup.category == "conditionGroup") {
            let localGroup = conditionOrGroup as ConditionGroup;
            return (
              <div key={`condition${localGroup.id}`} className="c1 gap10 fade">
                <ConditionGroupPicker
                  level={level + 1}
                  sections={sections}
                  current={localGroup}
                  onChange={(newValue) => {
                    localGroup = newValue;
                    if (localGroup.conditions?.length == 0) {
                      group.conditions?.splice(index, 1);
                    }
                    onValueChange(group);
                  }}
                ></ConditionGroupPicker>
                {button}
              </div>
            );
          } else {
            let condition = conditionOrGroup as DocumentCondition;
            return (
              <div key={`condition${condition.id}`} className="c1 gap10 fade">
                <div
                  className="grid gap10 aend card"
                  style={{ gridTemplateColumns: "1fr 1fr 1fr auto  " }}
                >
                  <VariableInputPicker
                    name="A"
                    sections={sections}
                    toolbarId={condition.id}
                    value={condition?.a}
                    onChange={(newValue) => {
                      condition.a = newValue.trim();
                      onChange({ ...group });
                    }}
                  />

                  <Input
                    value={condition?.operator}
                    name="Opérateur"
                    type="select"
                    options={Object.values(conditionOperators).map(
                      (operator) => {
                        return { value: operator.id, label: operator.name };
                      }
                    )}
                    onChange={(newValue) => {
                      condition.operator = newValue;
                      onChange({ ...group });
                    }}
                  />

                  <VariableInputPicker
                    name="B"
                    toolbarId={condition.id}
                    sections={sections}
                    value={condition?.b}
                    onChange={(newValue) => {
                      condition.b = newValue.trim();
                      onChange({ ...group });
                    }}
                  />

                  {/* <Button onClick={() => {}}>Ou</Button> */}
                  <IconButton
                    onClick={() => {
                      group.conditions?.splice(index, 1);
                      onValueChange(group);
                    }}
                  >
                    <MdDeleteOutline />
                  </IconButton>
                </div>
                {button}
              </div>
            );
          }
        })}
      </div>

      {level == 0 && (
        <div style={{ width: "50px" }}>
          <Button
            type={"tertiary"}
            onClick={() => {
              group.conditions?.push({
                id: uid(),
                category: "conditionGroup",
                type: "or",
                name: "Condition",
                creator: getUserId(),
                conditions: [
                  {
                    id: uid(),
                    category: "condition",
                    a: "",
                    operator: "=",
                    b: "",
                    creator: getUserId(),
                  },
                ],
              });
              onValueChange(group);
            }}
          >
            Ou
          </Button>
        </div>
      )}
    </div>
  );
}

function ConditionReplacementPicker({
  sections,
  current,
  onChange,
}: {
  sections: (FieldsSection | null | undefined)[];
  current: DocumentVariable;
  onChange: (newValue: DocumentVariable) => void;
}) {
  const [variable, setVariable] = useState<DocumentVariable>(current);
  function setValue(newValue: DocumentVariable) {
    setVariable({ ...newValue });
    onChange({ ...newValue });
  }
  return (
    <div className="c1 gap10">
      <VariableInputPicker
        name={"Si la condition est respectée"}
        value={variable.conditionReplacement || ""}
        sections={sections}
        toolbarId={uid()}
        onChange={(newValue) => {
          variable.conditionReplacement = newValue;
          onChange({ ...variable });
        }}
      ></VariableInputPicker>
      <VariableInputPicker
        name={"Sinon"}
        value={variable.conditionElseReplacement || ""}
        sections={sections}
        toolbarId={uid()}
        onChange={(newValue) => {
          variable.conditionElseReplacement = newValue;
          onChange({ ...variable });
        }}
      ></VariableInputPicker>
    </div>
  );
}

function ConditionGroupPickerButton({
  current,
  sections,
  toolbarId,
  onChange,
}: {
  current: ConditionGroup | null | undefined;
  sections: (FieldsSection | null | undefined)[];
  toolbarId?: string;
  onChange: (newValue: ConditionGroup | null | undefined) => void;
}) {
  let [conditionGroup, setConditionGroup] = useState(current);

  async function openConditionGroupPicker({
    onConfirm,
  }: {
    onConfirm: (group: ConditionGroup) => void;
  }) {
    let group = jsonAbstract(conditionGroup) || {
      id: uid(),
      name: "Condition",
      category: "conditionGroup",
      type: "and",
      creator: getUserId(),
      conditions: [
        {
          id: uid(),
          category: "condition",
          a: "",
          b: "",
          operator: "=",
          creator: getUserId(),
        },
      ],
    };

    await openDrawer({
      id: `documentConditionDialog${toolbarId}`,
      title: "Ajouter condition",
      additionalClasses: ["medium"],
      confirmBeforeClose: true,

      content: (
        <ConditionGroupPicker
          level={0}
          sections={sections || []}
          current={group}
          onChange={(newValue) => {
            conditionGroup = newValue;
          }}
        ></ConditionGroupPicker>
      ),
      onConfirm: async () => {
        onConfirm(group);
      },
    });
  }

  return (
    <div className="field">
      <div className="label">Condition</div>

      <Button
        type={"tertiary"}
        onClick={async () => {
          await openConditionGroupPicker({
            onConfirm: (newValue) => {
              setConditionGroup({ ...newValue });
              onChange({ ...newValue });
            },
          });
        }}
      >
        Choisir des conditions ({conditionGroup?.conditions?.length || 0})
      </Button>
    </div>
  );
}

function parseConditionGroup(group: any): ConditionGroup | null {
  if (!group) return null;
  if (typeof group == "object") return group;
  try {
    return JSON.parse(group);
  } catch (e) {
    return null;
  }
}
function stringifyConditionGroup(group: any): string | null {
  if (typeof group == "object") return JSON.stringify(group);
  if (typeof group == "string") return group;
  return null;
}

function shouldShowField({
  patient,
  reference,
  field,
  fieldAnswers,
}: {
  patient: Patient | null | undefined;
  reference: Reference | null | undefined;
  fieldAnswers: (FieldAnswer | null | undefined)[];
  field: Field | null | undefined;
}): any {
  if (!field) return false;
  let group = parseConditionGroup(field?.conditionGroupStr);
  if (!group) return true;
  if (!group.conditions?.length) return true;
  const condGroup = evalConditionGroup({
    group,
    patientDocument: {
      creator: USER.get(),
      patient,
      content: "",
      status: "pending",
      refAnswer: {
        patient,
        reference,
        creator: USER.get(),
        fieldAnswers: fieldAnswers,
        
      },
    },
  });
  console.log("condGroup", field.name, condGroup);
  return condGroup;
}

function shouldShowSection({
  patient,
  reference,
  section,
  fieldAnswers,
}: {
  patient: Patient | null | undefined;
  reference: Reference | null | undefined;
  fieldAnswers: (FieldAnswer | null | undefined)[];
  section: FieldsSection | null | undefined;
}): any {
  if (!section) return false;
  let group = parseConditionGroup(section?.conditionGroupStr);
  if (!group) return true;
  if (!group.conditions?.length) return true;
  return evalConditionGroup({
    group,
    patientDocument: {
      creator: USER.get(),
      patient,
      content: "",
      status: "pending",
      refAnswer: {
        patient,
        reference,
        creator: USER.get(),
        fieldAnswers: fieldAnswers,
        
      },
    },
  });
}

export {
  ConditionGroupPicker,
  ConditionGroupPickerButton,
  ConditionReplacementPicker,
  parseConditionGroup,
  shouldShowField,
  shouldShowSection,
  stringifyConditionGroup,
};
